import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Form } from "@/components/Form";
import { useAluno } from "@/hooks/alunos";
import { Input, Select } from "@/components/Form";
import { Button } from "@/components/Common";
import { Row, Col } from "@/components/Grid";
import toastify from "@/services/toastify";

import {
  FormContainer,
  FormSection,
  FormHeader,
} from "./styles";

export default function ContaBancaria() {
  const { data: user } = useSelector(state => state.auth);
  const { aluno, createContaBancaria, updateContaBancaria, fetchBancos } = useAluno();
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBancos, setLoadingBancos] = useState(false);
  const [formData, setFormData] = useState(null);

  const contaBancaria = aluno?.contas_bancarias?.[0] || null;
  const isEditing = !!contaBancaria;

  const tiposContaOptions = [
    { id: "CHECKING", title: "Conta Corrente" },
    { id: "SAVINGS", title: "Conta Poupança" },
    { id: "PAYMENT", title: "Conta Salário" },
  ];

  const propriaOptions = [
    { id: 1, title: "Própria" },
    { id: 0, title: "Responsável" },
  ];

  function handleOnChange(event) {
    const { name, value } = event.target;
    setFormData(state => {
      return { ...state, [name]: value };
    });
  }

  const getInitialData = useCallback(() => {
    if (contaBancaria) {
      return {
        codigo_banco: contaBancaria.codigo_banco,
        agencia: contaBancaria.agencia,
        conta_corrente: contaBancaria.conta_corrente,
        dv: contaBancaria.dv,
        tipo_conta: contaBancaria.tipo_conta,
        propria: contaBancaria.propria,
        chave_pix: contaBancaria.chave_pix || "",
      };
    }
    return {
      codigo_banco: "",
      agencia: "",
      conta_corrente: "",
      dv: "",
      tipo_conta: "CHECKING",
      propria: 1,
      chave_pix: "",
    };
  }, [contaBancaria]);

  const loadBancos = useCallback(async () => {
    setLoadingBancos(true);
    try {
      const bancosData = await fetchBancos();
      const bancosFormatados = bancosData.map(banco => ({
        id: banco.code,
        title: `${banco.code} - ${banco.short_name}`
      }));
      setBancos(bancosFormatados);
    } catch (error) {
      console.error("Erro ao carregar bancos:", error);
      toastify("Erro ao carregar lista de bancos", "error");
    } finally {
      setLoadingBancos(false);
    }
  }, [fetchBancos]);

  useEffect(() => {
    loadBancos();
  }, [loadBancos]);

  // Define os dados iniciais do formulário quando o aluno for carregado
  useEffect(() => {
    if (aluno) {
      setFormData(getInitialData());
    }
  }, [aluno, getInitialData]);

  async function handleSubmit(data) {
    setLoading(true);

    try {
      const formattedData = {
        ...formData,
        propria: parseInt(formData.propria, 10),
      };

      if (isEditing) {
        await updateContaBancaria(contaBancaria.id, formattedData);
      } else {
        await createContaBancaria(user.perfil_id, formattedData);
      }

      toastify(
        isEditing
          ? "Conta bancária atualizada com sucesso!"
          : "Conta bancária criada com sucesso!",
        "success"
      );
    } catch (error) {
      console.error("Erro ao salvar conta bancária:", error);
      toastify(
        isEditing
          ? "Erro ao atualizar conta bancária"
          : "Erro ao criar conta bancária",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormContainer>
      <FormSection>
        {formData && (
          <Form onSubmit={handleSubmit} initialData={formData}>
            <Row hascols>
              <Col md="1/2">
                <Select
                  name="codigo_banco"
                  label="Banco *"
                  options={bancos}
                  loading={loadingBancos}
                  placeholder="Selecione o banco"
                  disabled={loadingBancos}
                  value={formData.codigo_banco}
                  onChange={handleOnChange}
                />
              </Col>
              <Col md="1/2">
                <Select
                  name="tipo_conta"
                  label="Tipo de Conta *"
                  options={tiposContaOptions}
                  placeholder="Selecione o tipo de conta"
                  value={formData.tipo_conta}
                  onChange={handleOnChange}
                />
              </Col>
            </Row>

            <Row hascols>
              <Col md="1/3">
                <Input
                  name="agencia"
                  label="Agência *"
                  placeholder="Ex: 0001"
                  maxLength="10"
                  value={formData.agencia}
                  onChange={handleOnChange}
                />
              </Col>
              <Col md="1/2">
                <Input
                  name="conta_corrente"
                  label="Número da Conta *"
                  placeholder="Ex: 123456789"
                  maxLength="20"
                  value={formData.conta_corrente}
                  onChange={handleOnChange}
                />
              </Col>
              <Col md="1/6">
                <Input
                  name="dv"
                  label="DV *"
                  placeholder="Ex: 1"
                  maxLength="2"
                  value={formData.dv}
                  onChange={handleOnChange}
                />
              </Col>
            </Row>

            <Row hascols>
              <Col md="1/2">
                <Select
                  name="propria"
                  label="Titular da Conta *"
                  options={propriaOptions}
                  placeholder="Selecione o titular"
                  value={formData.propria}
                  onChange={handleOnChange}
                />
              </Col>
              <Col md="1/2">
                <Input
                  name="chave_pix"
                  label="Chave PIX (opcional)"
                  placeholder="CPF, e-mail, telefone ou chave aleatória"
                  maxLength="50"
                  value={formData.chave_pix}
                  onChange={handleOnChange}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <div style={{ marginTop: "24px", display: "flex", gap: "16px" }}>
                  <Button
                    type="submit"
                    variant="cyan"
                    loading={loading}
                    disabled={loading || loadingBancos}
                  >
                    {isEditing ? "Atualizar Conta" : "Cadastrar Conta"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </FormSection>
    </FormContainer>
  );
}
